import type { Card } from '../types'
import { RuleEngine } from './RuleEngine'

type ShuffleMode = 'random' | 'sequential' | 'spaced-repetition'

/**
 * DeckManager - 牌堆管理器
 * 负责加载卡片、洗牌、抽牌、进度跟踪、答案提交
 */
export class DeckManager {
  private cards: Card[] = []
  private currentIndex: number = 0
  private history: number[] = [] // 已访问的索引历史
  private ruleEngine: RuleEngine

  constructor() {
    this.ruleEngine = new RuleEngine()
  }

  /**
   * 加载牌堆
   */
  loadDeck(cards: Card[]): void {
    this.cards = [...cards]
    this.currentIndex = 0
    this.history = []
  }

  /**
   * 洗牌
   */
  shuffle(mode: ShuffleMode = 'random'): void {
    if (mode === 'sequential') {
      // 保持原顺序
      return
    }

    if (mode === 'random') {
      // Fisher-Yates 洗牌
      for (let i = this.cards.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[this.cards[i], this.cards[j]] = [this.cards[j]!, this.cards[i]!]
      }
    }

    if (mode === 'spaced-repetition') {
      // 简单实现：按难度排序（easy -> medium -> hard）
      const difficultyOrder = { easy: 0, medium: 1, hard: 2 }
      this.cards.sort((a, b) => difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty])
    }
  }

  /**
   * 抽下一张牌
   */
  drawNext(): Card | null {
    if (this.currentIndex >= this.cards.length) {
      return null // 牌堆已空
    }

    this.history.push(this.currentIndex)
    const card = this.cards[this.currentIndex]!
    this.currentIndex++
    return card
  }

  /**
   * 回到上一张牌
   */
  drawPrev(): Card | null {
    if (this.history.length === 0) {
      return null
    }

    // 弹出当前位置
    this.history.pop()

    if (this.history.length === 0) {
      // 回到第一张之前, 无卡可返回
      this.currentIndex = 0
      return null
    }

    // 返回上一张卡
    const prevIndex = this.history[this.history.length - 1]!
    this.currentIndex = prevIndex + 1
    return this.cards[prevIndex] ?? null
  }

  /**
   * 获取当前进度
   */
  getProgress(): { current: number; total: number } {
    return {
      current: this.currentIndex,
      total: this.cards.length,
    }
  }

  /**
   * 提交答案
   */
  submitAnswer(cardId: string, choiceIndex: number) {
    const card = this.cards.find(c => c.id === cardId)
    if (!card) {
      throw new Error(`Card not found: ${cardId}`)
    }

    return this.ruleEngine.evaluate(card, choiceIndex)
  }

  /**
   * 获取当前卡片（不推进）
   */
  getCurrentCard(): Card | null {
    if (this.currentIndex === 0 || this.currentIndex > this.cards.length) {
      return null
    }
    return this.cards[this.currentIndex - 1] ?? null
  }

  /**
   * 重置牌堆
   */
  reset(): void {
    this.currentIndex = 0
    this.history = []
  }

  /**
   * 获取所有卡片
   */
  getAllCards(): Card[] {
    return [...this.cards]
  }
}
